// Toast — in-house toast surface.md.
// Pure presentational stack: renders a bottom-right column, up to 3 visible,
// older ones suppressed. The store binding + auto-dismiss live in the shell
// host (shell/ToastHost.tsx); this component only renders the passed `toasts`
// and reports dismiss intent via `onDismiss`.
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from './Icon.js';
import { IconButton } from './IconButton.js';

export type ToastTone = 'info' | 'success' | 'error';

export interface ToastView {
  id: string;
  tone: ToastTone;
  text: string;
}

const MAX_VISIBLE = 3;
const TOAST_Z_INDEX = 1100;

const ICON_BY_TONE: Record<ToastTone, IconName> = {
  info: 'circle-dot',
  success: 'check-circle-2',
  error: 'alert-triangle',
};

function bgForTone(tone: ToastTone): string {
  if (tone === 'error') return 'var(--status-error-bg)';
  if (tone === 'success') return 'var(--bg-2)';
  return 'var(--bg-2)';
}

function fgForTone(tone: ToastTone): string {
  if (tone === 'error') return 'var(--status-error-fg)';
  if (tone === 'success') return 'var(--fg-1)';
  return 'var(--fg-1)';
}

function ToastRow({ toast, onDismiss }: { toast: ToastView; onDismiss: (id: string) => void }) {
  const { t } = useTranslation('common');
  return (
    <div
      role={toast.tone === 'error' ? 'alert' : 'status'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 'var(--r-2)',
        background: bgForTone(toast.tone),
        color: fgForTone(toast.tone),
        border: '1px solid var(--border-1)',
        boxShadow: 'var(--shadow-2, 0 6px 20px rgba(0,0,0,0.18))',
        minWidth: 260,
        maxWidth: 360,
      }}
    >
      <Icon name={ICON_BY_TONE[toast.tone]} size={16} />
      <div style={{ flex: 1, fontSize: 13 }}>{toast.text}</div>
      <IconButton icon="x" label={t('actions.close')} onClick={() => onDismiss(toast.id)} />
    </div>
  );
}

export function Toast({
  toasts,
  onDismiss,
}: {
  toasts: ToastView[];
  onDismiss: (id: string) => void;
}) {
  // Show only the most-recent MAX_VISIBLE; the older ones are logically still
  // in the store (so auto-dismiss timers continue to land) but not rendered.
  const visible = toasts.slice(-MAX_VISIBLE);
  return (
    <div
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        // INVARIANT: toasts must stay above every app overlay so mutation
        // errors remain visible while a drawer, modal, or picker is open.
        zIndex: TOAST_Z_INDEX,
        pointerEvents: visible.length === 0 ? 'none' : 'auto',
      }}
    >
      {visible.map((t) => (
        <ToastRow key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
